<template>
  <div class="page-container">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
      <div class="breadcrumb">
        <router-link to="/" class="breadcrumb-link">{{ i18n.locale === 'zh' ? '首页' : 'Home' }}</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ i18n.t('pages.cron.title') }}</span>
      </div>
      <div class="header-content">
        <div class="header-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <h1 class="page-title">{{ i18n.t('pages.cron.title') }}</h1>
        <p class="page-subtitle">{{ i18n.t('pages.cron.subtitle') }}</p>
      </div>
    </header>

    <main class="page-content">
      <div class="cron-card">
        <div class="preset-section">
          <label class="preset-label">{{ i18n.locale === 'zh' ? '常用表达式' : 'Presets' }}</label>
          <div class="preset-grid">
            <button
              v-for="preset in presets"
              :key="preset.value"
              @click="applyPreset(preset.value)"
              :class="['preset-btn', { active: cronExpression === preset.value }]"
            >
              <span class="preset-expr">{{ preset.value }}</span>
              <span class="preset-desc">{{ i18n.locale === 'zh' ? preset.descZh : preset.descEn }}</span>
            </button>
          </div>
        </div>

        <div class="builder-section">
          <label class="builder-label">{{ i18n.locale === 'zh' ? '自定义表达式' : 'Custom Expression' }}</label>
          <div class="cron-input-row">
            <div class="cron-field" v-for="field in cronFields" :key="field.key">
              <label class="field-label">{{ field.label }}</label>
              <input
                type="text"
                v-model="field.value"
                :placeholder="field.placeholder"
                class="field-input"
                @input="updateCronFromFields"
              />
              <span class="field-hint">{{ field.hint }}</span>
            </div>
          </div>
          <div class="cron-result">
            <label class="result-label">Cron {{ i18n.locale === 'zh' ? '表达式' : 'Expression' }}</label>
            <div class="cron-expression">{{ cronExpression }}</div>
          </div>
        </div>

        <div class="next-runs-section">
          <label class="runs-label">{{ i18n.locale === 'zh' ? '接下来 10 次执行时间' : 'Next 10 Runs' }}</label>
          <div class="runs-list">
            <div v-for="(run, idx) in nextRuns" :key="idx" class="run-item">
              <span class="run-index">{{ idx + 1 }}</span>
              <span class="run-time">{{ run }}</span>
            </div>
          </div>
        </div>

        <div class="action-row">
          <button @click="copyCron" class="copy-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            {{ i18n.locale === 'zh' ? '复制表达式' : 'Copy Expression' }}
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'CronGeneratorPage',
  inject: ['i18n'],
  data() {
    return {
      cronExpression: '0 * * * *',
      cronFields: [
        { key: 'second', label: this.i18n.locale === 'zh' ? '秒' : 'Sec', value: '0', placeholder: '0-59', hint: this.i18n.locale === 'zh' ? '默认 0' : 'default 0' },
        { key: 'minute', label: this.i18n.locale === 'zh' ? '分' : 'Min', value: '*', placeholder: '0-59', hint: this.i18n.locale === 'zh' ? '每分' : 'every min' },
        { key: 'hour', label: this.i18n.locale === 'zh' ? '时' : 'Hour', value: '*', placeholder: '0-23', hint: this.i18n.locale === 'zh' ? '每小时' : 'every hour' },
        { key: 'day', label: this.i18n.locale === 'zh' ? '日' : 'Day', value: '*', placeholder: '1-31', hint: this.i18n.locale === 'zh' ? '每天' : 'every day' },
        { key: 'month', label: this.i18n.locale === 'zh' ? '月' : 'Mon', value: '*', placeholder: '1-12', hint: this.i18n.locale === 'zh' ? '每月' : 'every month' },
        { key: 'week', label: this.i18n.locale === 'zh' ? '周' : 'Week', value: '*', placeholder: '0-6', hint: this.i18n.locale === 'zh' ? '每天' : 'every day' }
      ],
      nextRuns: [],
      presets: [
        { value: '* * * * *', descZh: '每分钟', descEn: 'Every minute' },
        { value: '0 * * * *', descZh: '每小时', descEn: 'Hourly' },
        { value: '0 0 * * *', descZh: '每天零点', descEn: 'Daily at midnight' },
        { value: '0 0 * * 0', descZh: '每周日', descEn: 'Weekly on Sunday' },
        { value: '0 0 1 * *', descZh: '每月1号', descEn: '1st of month' },
        { value: '0 9 * * 1-5', descZh: '工作日早9点', descEn: 'Weekdays 9AM' }
      ]
    }
  },
  created() {
    this.calculateNextRuns()
  },
  methods: {
    applyPreset(value) {
      this.cronExpression = value
      const parts = value.split(' ')
      if (parts.length >= 5) {
        this.cronFields[0].value = parts[0] || '0'
        this.cronFields[1].value = parts[1] || '*'
        this.cronFields[2].value = parts[2] || '*'
        this.cronFields[3].value = parts[3] || '*'
        this.cronFields[4].value = parts[4] || '*'
        this.cronFields[5].value = parts[5] || '*'
      }
      this.calculateNextRuns()
    },
    updateCronFromFields() {
      this.cronExpression = this.cronFields.map(f => f.value || '*').join(' ')
      this.calculateNextRuns()
    },
    calculateNextRuns() {
      this.nextRuns = []
      const now = new Date()
      let count = 0
      const maxIterations = 1000
      let iterations = 0

      const parts = this.cronExpression.split(' ')
      if (parts.length < 5) {
        return
      }

      const [second, minute, hour, day, month, week = '*'] = parts

      const next = new Date(now)
      next.setSeconds(next.getSeconds() + 1)

      while (count < 10 && iterations < maxIterations) {
        iterations++
        const matchSecond = this.matchCronField(second, next.getSeconds(), 0, 59)
        const matchMinute = this.matchCronField(minute, next.getMinutes(), 0, 59)
        const matchHour = this.matchCronField(hour, next.getHours(), 0, 23)
        const matchDay = this.matchCronField(day, next.getDate(), 1, 31)
        const matchMonth = this.matchCronField(month, next.getMonth() + 1, 1, 12)
        const matchWeek = this.matchCronField(week, next.getDay(), 0, 6)

        if (matchSecond && matchMinute && matchHour && matchDay && matchMonth && matchWeek) {
          this.nextRuns.push(this.formatDate(next))
          count++
        }

        next.setSeconds(next.getSeconds() + 1)
        if (next.getTime() - now.getTime() > 86400000 * 366) {
          break
        }
      }
    },
    matchCronField(field, value, min, max) {
      if (field === '*') {
        return true
      }

      if (field.includes(',')) {
        return field.split(',').some(f => this.matchCronField(f.trim(), value, min, max))
      }

      if (field.includes('/')) {
        const [range, step] = field.split('/')
        if (range === '*') {
          return value % parseInt(step) === 0
        }
        return true
      }

      if (field.includes('-')) {
        const [start, end] = field.split('-').map(Number)
        return value >= start && value <= end
      }

      return parseInt(field) === value
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },
    async copyCron() {
      try {
        await navigator.clipboard.writeText(this.cronExpression)
      } catch (e) {
        console.error('Copy failed', e)
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  min-height: 100vh;
  padding: 40px 24px 60px;
  position: relative;
  overflow: hidden;
}

.bg-grid {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image:
    linear-gradient(rgba(232, 255, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(232, 255, 0, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.bg-glow {
  position: fixed;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 240, 255, 0.08) 0%, transparent 70%);
  top: 20%;
  left: -100px;
  filter: blur(100px);
  pointer-events: none;
  z-index: 0;
}

.page-header {
  max-width: 800px;
  margin: 0 auto 50px;
  position: relative;
  z-index: 1;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 30px;
  font-family: var(--font-mono);
  font-size: 12px;
}

.breadcrumb-link { color: var(--text-secondary); text-decoration: none; transition: color 0.3s; }
.breadcrumb-link:hover { color: var(--accent-primary); }
.breadcrumb-sep { color: var(--border-color); }
.breadcrumb-current { color: var(--accent-success); }

.header-content { text-align: center; }

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  color: var(--accent-success);
  margin-bottom: 20px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 48px;
  letter-spacing: 2px;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle { font-size: 16px; color: var(--text-secondary); font-weight: 300; }

.page-content { max-width: 800px; margin: 0 auto; position: relative; z-index: 1; }

.cron-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 40px;
}

.preset-section { margin-bottom: 36px; }

.preset-label, .builder-label, .runs-label, .result-label {
  display: block;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.preset-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.preset-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 14px 18px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
}

.preset-btn:hover { border-color: var(--accent-success); }
.preset-btn.active {
  background: var(--accent-success);
  color: var(--bg-primary);
  border-color: var(--accent-success);
}

.preset-expr {
  font-family: var(--font-mono);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.preset-btn.active .preset-expr,
.preset-btn.active .preset-desc { color: var(--bg-primary); }

.preset-desc {
  font-size: 12px;
  opacity: 0.7;
}

.builder-section {
  padding: 28px;
  background: var(--bg-secondary);
  border-radius: 16px;
  margin-bottom: 28px;
}

.cron-input-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.cron-field {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  margin-bottom: 6px;
  text-transform: uppercase;
}

.field-input {
  padding: 10px 12px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-family: var(--font-mono);
  font-size: 14px;
  text-align: center;
}

.field-input:focus { outline: none; border-color: var(--accent-success); }

.field-hint {
  font-size: 10px;
  color: var(--text-secondary);
  margin-top: 4px;
  text-align: center;
  opacity: 0.6;
}

.cron-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 12px;
}

.cron-expression {
  font-family: var(--font-mono);
  font-size: 20px;
  color: var(--accent-success);
  font-weight: 600;
  letter-spacing: 2px;
}

.next-runs-section { margin-bottom: 28px; }

.runs-list {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
}

.run-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-color);
}

.run-item:last-child { border-bottom: none; }

.run-index {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-card);
  border-radius: 6px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
  margin-right: 16px;
}

.run-time {
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--text-primary);
}

.action-row {
  display: flex;
  justify-content: flex-end;
}

.copy-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  color: var(--accent-success);
  border: 1px solid var(--accent-success);
  border-radius: 10px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-btn:hover {
  background: var(--accent-success);
  color: var(--bg-primary);
}

@media (max-width: 768px) {
  .page-container { padding: 24px 16px 40px; }
  .page-title { font-size: 36px; }
  .cron-card { padding: 24px; }
  .cron-input-row { grid-template-columns: repeat(3, 1fr); }
  .preset-grid { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
  .cron-input-row { grid-template-columns: repeat(2, 1fr); }
}
</style>
